import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AlertCircle, UserPlus } from 'lucide-react';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';

import { PageContainer } from '../../../common/components/PageContainer';
import { useAuthContext } from '../../../common/hooks/use-auth-context.hook';
import { TeamMember, TeamSeats, useTeamApi } from '../../../common/hooks/use-team-api.hook';
import { RelativeTime, SectionCard, StatusBadge } from '../../../components/design-system/admin';
import { Invitations } from '../../../modules/superadmin/users/components/invitations.component';
import { TeamInviteDialog } from '../../../modules/vendor/team/TeamInviteDialog';
import { seatsLeft } from '../../../modules/vendor/team/seats';
import { TeamSeatsPanel } from '../../../modules/vendor/team/TeamSeatsPanel';

/** Home > Team: who can sign in to this account, and inviting more. */
export const TeamPage = () => {
    const { t } = useTranslation();
    const teamApi = useTeamApi();
    const { user } = useAuthContext();

    const [members, setMembers] = useState<TeamMember[] | null>(null);
    const [seats, setSeats] = useState<TeamSeats | null>(null);
    const [loadFailed, setLoadFailed] = useState(false);
    const [inviteOpen, setInviteOpen] = useState(false);
    // Bumped after an invitation, so the list below reloads.
    const [version, setVersion] = useState(0);

    const load = async () => {
        try {
            const team = await teamApi.getTeam();
            setMembers(team.members || []);
            setSeats(team.seats ?? null);
            setLoadFailed(false);
        } catch (error) {
            console.error('Error loading team:', error);
            setLoadFailed(true);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [version]);

    const left = seatsLeft(seats);
    const full = left === 0;

    const status = (m: TeamMember) =>
        !m.isEnabled
            ? <StatusBadge tone="danger">{t('VENDOR_TEAM.STATUS_DISABLED')}</StatusBadge>
            : !m.isVerified
                ? <StatusBadge tone="warning">{t('VENDOR_TEAM.STATUS_UNVERIFIED')}</StatusBadge>
                : <StatusBadge tone="success">{t('VENDOR_TEAM.STATUS_ACTIVE')}</StatusBadge>;

    return (
        <PageContainer breadcrumbs={[{ label: t('SIDEBAR.ACCOUNT.TITLE', 'Account') }, { label: t('VENDOR_TEAM.TITLE') }]}>
            <div className="flex flex-col gap-6">
                <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                    <div>
                        <h1 className="font-heading text-2xl font-medium tracking-tight">{t('VENDOR_TEAM.TITLE')}</h1>
                        <p className="mt-1 max-w-2xl text-sm text-muted-foreground">{t('VENDOR_TEAM.SUBTITLE')}</p>
                    </div>
                    <Button onClick={() => setInviteOpen(true)} disabled={full}>
                        <UserPlus />
                        {t('VENDOR_TEAM.INVITE')}
                    </Button>
                </div>

                {seats && <TeamSeatsPanel seats={seats} />}

                {full && (
                    <Alert data-testid="team-seats-full">
                        <AlertCircle />
                        <AlertDescription>{t('VENDOR_TEAM.SEATS_FULL')}</AlertDescription>
                    </Alert>
                )}

                <SectionCard title={t('VENDOR_TEAM.MEMBERS_TITLE')} description={t('VENDOR_TEAM.MEMBERS_DESCRIPTION')} flush testId="team-members">
                    {loadFailed ? (
                        <div className="flex items-center justify-between gap-3 px-6 py-4">
                            <p className="text-sm text-muted-foreground">{t('VENDOR_TEAM.LOAD_FAILED')}</p>
                            <Button variant="outline" size="sm" onClick={load}>{t('ADMIN_SHELL.COMMON.RETRY')}</Button>
                        </div>
                    ) : !members ? (
                        <div className="px-6 pb-4"><Skeleton className="h-24 rounded-xl" /></div>
                    ) : (
                        <ul className="divide-y border-t">
                            {members.map((m) => {
                                const name = [m.firstname, m.lastname].filter(Boolean).join(' ');
                                const isMe = !!user?.email && user.email.toLowerCase() === m.email.toLowerCase();
                                return (
                                    <li key={m._id} className="flex flex-col gap-2 px-6 py-3 sm:flex-row sm:items-center sm:justify-between" data-testid="team-member">
                                        <div className="flex min-w-0 flex-col">
                                            <span className="truncate text-sm font-medium">
                                                {name || m.email}
                                                {isMe && <span className="ms-2 text-xs font-normal text-muted-foreground">{t('VENDOR_TEAM.YOU')}</span>}
                                            </span>
                                            {name && <span className="truncate text-xs text-muted-foreground">{m.email}</span>}
                                        </div>
                                        <div className="flex shrink-0 items-center gap-4 text-xs text-muted-foreground">
                                            {status(m)}
                                            {m.createdAt && <span>{t('VENDOR_TEAM.JOINED')} <RelativeTime value={m.createdAt} /></span>}
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </SectionCard>

                <Invitations key={version} source={teamApi} />
            </div>

            <TeamInviteDialog seatsLeft={left} open={inviteOpen} onOpenChange={setInviteOpen} onInvited={() => setVersion((v) => v + 1)} />
        </PageContainer>
    );
};

export default TeamPage;
